import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Governed campaigns index.
 *
 * Replaces the retired simple-campaign board on 2026-10-23. Lists
 * every governed campaign the current user's org owns, each linking
 * to the Creative Studio Panel inside the campaign detail page.
 *
 * P5 (2026-11-02) — adds a `+ New campaign` inline form directly on
 * this page (it was previously only accessible from Command Center).
 */

function InlineCreateCampaign({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(null);
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [objective, setObjective] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/products");
        const list = r.data?.products || r.data || [];
        setProducts(list);
        if (list.length === 1 && !productId) setProductId(list[0].id);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (!productId || !name.trim()) {
      setErr("Pick a product and name the campaign.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/campaigns", {
        product_id: productId,
        name: name.trim(),
        objective: objective.trim() || null,
      });
      setName("");
      setObjective("");
      setOpen(false);
      if (onCreated) await onCreated();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button
        variant="default"
        onClick={() => setOpen(true)}
        data-testid="new-campaign-cta"
      >
        + New campaign
      </Button>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="new-campaign-form"
      style={{
        marginBottom: 14, padding: 12, borderRadius: 8,
        border: "1px dashed var(--bo-line)",
        background: "var(--bo-panel-alt, #faf8f2)",
        display: "grid", gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center" }}>
        <strong style={{ fontSize: 13 }}>New campaign</strong>
        <button
          type="button"
          onClick={() => { setOpen(false); setErr(null); }}
          data-testid="new-campaign-cancel"
          style={{ background: "none", border: 0, cursor: "pointer",
                   color: "var(--bo-muted)", fontSize: 12 }}
        >
          Cancel
        </button>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Product</label>
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          data-testid="new-campaign-product"
          style={{ padding: "6px 8px", fontSize: 13 }}
        >
          <option value="">— pick a product —</option>
          {(products || []).map((p) => (
            <option key={p.id} value={p.id}>{p.name || p.id}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Campaign name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. The Well — Q4 waitlist growth"
          data-testid="new-campaign-name"
          style={{ padding: "6px 8px", fontSize: 13 }}
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Objective (optional)</label>
        <textarea
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          placeholder="What does this campaign need to accomplish?"
          rows={3}
          data-testid="new-campaign-objective"
          style={{ padding: "6px 8px", fontSize: 13,
                   fontFamily: "inherit" }}
        />
      </div>
      {err && (
        <div
          data-testid="new-campaign-error"
          style={{ color: "crimson", fontSize: 12 }}
        >
          {typeof err === "string" ? err : err.message || JSON.stringify(err)}
        </div>
      )}
      <div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="new-campaign-submit"
        >
          {busy ? "Creating…" : "Create campaign"}
        </Button>
      </div>
    </form>
  );
}

export default function Campaigns() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/campaigns");
      setRows(r.data?.campaigns || []);
    } catch (e) {
      setErr(e?.response?.data?.detail?.error?.message || e.message);
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Shell>
      <div data-testid="campaigns-index" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                       alignItems: "center", marginBottom: 16 }}>
          <h1 style={{ fontSize: 22, margin: 0 }}>Campaigns</h1>
          <InlineCreateCampaign onCreated={load} />
        </div>
        {err && (
          <Card style={{ padding: 16, marginBottom: 16, color: "crimson" }}>
            Could not load campaigns: {err}
          </Card>
        )}
        {rows === null && (
          <Card style={{ padding: 16 }}>Loading campaigns…</Card>
        )}
        {Array.isArray(rows) && rows.length === 0 && (
          <Card style={{ padding: 16 }} data-testid="campaigns-empty">
            No campaigns yet. Click <strong>+ New campaign</strong> to create one.
          </Card>
        )}
        {Array.isArray(rows) && rows.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {rows.map((c) => (
              <Link
                key={c.id}
                to={`/brand-os/campaigns/${c.id}`}
                data-testid={`campaign-row-${c.id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card style={{ padding: 16, display: "flex",
                               justifyContent: "space-between",
                               alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>
                      {c.name || "(unnamed)"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#666",
                        fontFamily: "var(--bo-mono, monospace)",
                      }}
                      data-testid={`campaign-row-${c.id}-id`}
                    >
                      {c.id ? c.id.slice(0, 8) : ""} ·{" "}
                      status: {c.status || "draft"}
                    </div>
                  </div>
                  <Badge variant="secondary">{c.status || "draft"}</Badge>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
